const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/**
 * Returns the pluralization object key corresponding to the given number.
 *
 * @param count Count.
 *
 * @return Pluralization key.
 */
const getPluralizationKey = count => count === 1 ? 'one' : 'other';

/**
 * Returns an entry from locale data.
 *
 * @param entry Locale data.
 * @param count Pluralization count, if applicable.
 *
 * @return Entry string or object.
 */
const getEntry = (strings, key) => hasOwn(strings, key) ? strings[key] : key;

/**
 * Returns true if the given entry is a pluralization entry, or false otherwise.
 *
 * @param entry Entry to test.
 *
 * @return Whether entry is a pluralization entry.
 */
const isPluralizedEntry = entry => typeof entry === 'object' && 'one' in entry;

/**
 * Returns true if the given entry is a string entry, or false otherwise.
 *
 * @param entry Entry to test.
 *
 * @return Whether entry is a string entry.
 */
const isStringEntry = entry => typeof entry === 'string';

/**
 * Returns the resulting string from the given entry, incorporating pluralization if necessary.
 *
 * @param entry Entry string or object.
 * @param count Pluralization count, if applicable.
 *
 * @return Entry string.
 */
function getString(entry, count) {
  if (isPluralizedEntry(entry)) {
    if (typeof count !== 'number') {
      throw new TypeError('Expected count for PluralizedEntry');
    }
    return entry[getPluralizationKey(count)];
  }
  return entry;
}

/**
 * Returns string with variable substitution.
 *
 * @param string Original string.
 * @param variables Variables to replace.
 *
 * @return String with variables substituted.
 */
export const replaceVariables = (string, variables) => string.replace(/%{(\w+)}/g, (match, key) => hasOwn(variables, key) ? variables[key] : match);
class I18n {
  constructor() {
    let {
      strings
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.strings = Object.assign(Object.create(null), strings);
    this.t = this.t.bind(this);
  }

  /**
   * Returns the translated string by the given key.
   *
   * @param keyOrKeys Key or keys to retrieve.
   * @param variables Variables to substitute in string.
   *
   * @return Translated string.
   */

  t(keyOrKeys) {
    let variables = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const isSingular = !Array.isArray(keyOrKeys);
    const keys = isSingular ? [keyOrKeys] : keyOrKeys;
    const entries = keys.map(key => getEntry(this.strings, key));
    const strings = entries.map(entry => isPluralizedEntry(entry) ? getString(entry, variables?.count) : entry).map(entry => isStringEntry(entry) ? replaceVariables(entry, variables) : entry);
    return isSingular ? strings[0] : strings.flat();
  }
}

// eslint-disable-next-line no-underscore-dangle
const i18n = new I18n({
  strings: globalThis._locale_data
});
const {
  t
} = i18n;
export { I18n, i18n, t };